import { CaretDownIcon, StackIcon } from '@phosphor-icons/react';
import { Button, Popover } from '@skupreme/shared/ui';
import { ReadOnlyTip, useOrdersV2 } from '../../hooks/orders-v2';

export type BatchTarget = { id: string; name: string };

export type BatchSelectionBarProps = {
  isSelecting: boolean;
  /** False when the page holds nothing that could be batched. */
  canSelect: boolean;
  onStart: () => void;
  onCancel: () => void;
  selectedCount: number;
  /** The warehouse the first tick settled on; a batch ships from one place. */
  providerName?: string;
  onCreate: () => void;
  /** Open batches at the same warehouse. */
  targets: BatchTarget[];
  onAddToBatch: (batchId: string) => void;
  isBusy?: boolean;
};

export function BatchSelectionBar({
  isSelecting,
  canSelect,
  onStart,
  onCancel,
  selectedCount,
  providerName,
  onCreate,
  targets,
  onAddToBatch,
  isBusy,
}: BatchSelectionBarProps) {
  const { isV2 } = useOrdersV2();

  if (!isSelecting) {
    return canSelect ? (
      <ReadOnlyTip>
        <Button variant="pill" size="md" isDisabled={isV2} onPress={onStart}>
          <StackIcon size={12} weight="bold" />
          Create batch
        </Button>
      </ReadOnlyTip>
    ) : null;
  }

  return (
    <div className="sk:inline-flex sk:items-center sk:gap-3 sk:rounded-full sk:bg-fill-tertiary sk:py-1 sk:pr-1 sk:pl-3">
      <span className="sk:text-xs sk:text-fg-secondary">
        {selectedCount === 0 ? (
          'Pick the orders to batch'
        ) : (
          <>
            <span className="sk:font-medium sk:text-fg">
              {selectedCount} selected
            </span>
            {providerName ? ` · ${providerName}` : null}
          </>
        )}
      </span>

      <span className="sk:inline-flex sk:items-center sk:gap-1.5">
        <Button
          variant="primary"
          size="sm"
          /* The API refuses a batch of fewer than two orders. */
          isDisabled={selectedCount < 2 || isBusy}
          onPress={onCreate}
        >
          Create batch
        </Button>

        {targets.length > 0 ? (
          <Popover
            aria-label="Add to existing batch"
            placement="bottom end"
            trigger={
              <Button
                variant="pill"
                size="sm"
                isDisabled={selectedCount === 0 || isBusy}
              >
                Add to batch
                <CaretDownIcon size={10} weight="bold" />
              </Button>
            }
          >
            {({ close }) => (
              <div className="sk:flex sk:max-h-72 sk:w-52 sk:flex-col sk:overflow-y-auto">
                {targets.map(target => (
                  <Button
                    key={target.id}
                    variant="menu"
                    size="md"
                    onPress={() => {
                      onAddToBatch(target.id);
                      close();
                    }}
                  >
                    <span className="sk:min-w-0 sk:truncate">
                      {target.name}
                    </span>
                  </Button>
                ))}
              </div>
            )}
          </Popover>
        ) : null}

        <Button
          variant="ghost"
          size="sm"
          onPress={onCancel}
          isDisabled={isBusy}
        >
          Cancel
        </Button>
      </span>
    </div>
  );
}
